import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {groupBy, max, maxBy, sortBy} from 'lodash';
import type {IconName} from '@lightricks/react-design-system';
import {formattedDate, formattedUtcDate} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import SOCIAL_NETWORK_ICON from '@/config/socialNetworkIconMap';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import type {
  CreatorDeliverable,
  DeliverableState,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import type {TagColor} from '@/components/tag';
import {tagColorForDeliverableState} from './assetsCellStatus';

dayjs.extend(utc);

export interface KindGroup {
  state: DeliverableState;
  round: number | null;
  summary: string;
  dateSuffix: string | null;
  tagColor: TagColor;
}

export interface KindRollup {
  kind: string;
  icon: IconName;
  label: string;
  approvedCount: number;
  contractedCount: number;
  isComplete: boolean;
  frameCount: number | null;
  groups: KindGroup[];
}

const KIND_ORDER = [
  'photo',
  'video',
  'mixed_media',
  'blog',
  'feed',
  'reels',
  'story',
] as const;

// Swap-match slots only. Same allowlist as PPSuite `slots_for_counted_kind`.
const SWAP_MATCH_KINDS = new Set(['feed', 'reels', 'story']);

const KIND_LABELS: Record<string, {one: string; other: string}> = {
  photo: {one: 'Photo', other: 'Photos'},
  video: {one: 'Video', other: 'Videos'},
  blog: {one: 'Blog', other: 'Blogs'},
  mixed_media: {one: 'Story Upload', other: 'Story Uploads'},
  feed: {one: 'Post', other: 'Posts'},
  reels: {one: 'Reel', other: 'Reels'},
  story: {one: 'Story', other: 'Stories'},
};

const KIND_ICONS: Record<string, IconName> = {
  photo: 'Navigation-Photos-Light',
  video: 'Navigation-VideoCamera-Line',
  mixed_media: 'Social-InstagramStories',
  blog: 'Navigation-Book',
  feed: 'Navigation-Share',
  reels: 'Social-InstagramReels',
  story: 'Social-InstagramStories',
};

const FALLBACK_ICON: IconName = 'Actions-MediaReplace';

const NETWORK_ICON: Record<string, IconName> = {
  ...SOCIAL_NETWORK_ICON,
  linkedin: 'Social-LinkedIn',
  snapchat: 'Social-Snapchat',
};

// Longest first so tiktok_creator_marketplace wins over tiktok.
const NETWORK_KIND_PREFIXES = [
  'tiktok_creator_marketplace',
  'pinterest_organic',
  'instagram_business',
  'tiktok_v2',
  'instagram',
  'facebook',
  'pinterest',
  'youtube',
  'twitter',
  'linkedin',
  'snapchat',
  'tiktok',
  'amazon',
  'reddit',
  'twitch',
] as const;

type PostFormat = 'reel' | 'story' | 'post';

const STATE_ORDER: DeliverableState[] = [
  'pending',
  'rejected',
  'processing',
  'not_submitted',
  'accepted',
  'posted',
];

const STATE_PHRASE: Record<DeliverableState, string> = {
  pending: 'ready for review',
  rejected: 'edits requested',
  accepted: 'approved',
  posted: 'posted',
  processing: 'processing',
  not_submitted: 'awaiting upload',
};

function titleizeKind(kind: string): string {
  return kind.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function networkIcon(network: string | null | undefined): IconName | null {
  if (!network) {
    return null;
  }
  return NETWORK_ICON[network.toLowerCase()] ?? null;
}

function networkDisplayName(network: string | null | undefined): string | null {
  const icon = networkIcon(network);
  return icon ? icon.replace(/^Social-/, '') : null;
}

function isInstagram(network: string | null | undefined): boolean {
  return !!network?.toLowerCase().includes('instagram');
}

function networkKeyFromKind(kind: string): string | null {
  const lower = kind.toLowerCase();
  return (
    NETWORK_KIND_PREFIXES.find(
      (prefix) => lower === prefix || lower.startsWith(`${prefix}_`)
    ) ?? null
  );
}

function kindTokens(kind: string): string[] {
  return kind.toLowerCase().split('_');
}

function hasKindToken(kind: string, ...tokens: string[]): boolean {
  const parts = kindTokens(kind);
  return tokens.some((token) => parts.includes(token));
}

// Paid-gig kinds are `{format}_to_post` (stories_to_post, reels_to_post).
// Classify by underscore tokens so "stories" matches story and still beats
// the trailing `post` token. `feed` is the swap-matched alias with no post token.
function postFormat(kind: string): PostFormat | null {
  if (hasKindToken(kind, 'reel', 'reels')) {
    return 'reel';
  }
  if (hasKindToken(kind, 'story', 'stories')) {
    return 'story';
  }
  if (kind === 'feed' || hasKindToken(kind, 'post')) {
    return 'post';
  }
  return null;
}

function formatTypeKey(format: PostFormat, plural: boolean): string {
  if (format === 'reel') {
    return plural ? 'Reels' : 'Reel';
  }
  if (format === 'story') {
    return plural ? 'Stories' : 'Story';
  }
  return plural ? 'Posts' : 'Post';
}

function kindLabel(
  kind: string,
  campaignNetwork: string | null | undefined,
  count: number
): string {
  const plural = count > 1;
  const format = postFormat(kind);
  if (format) {
    const network = networkKeyFromKind(kind) ?? campaignNetwork ?? null;
    const name = networkDisplayName(network);
    const typeLabel = translate(formatTypeKey(format, plural));
    return name ? `${name} ${typeLabel}` : typeLabel;
  }
  const mapped = KIND_LABELS[kind];
  if (mapped) {
    return translate(plural ? mapped.other : mapped.one);
  }
  const fallback = titleizeKind(kind);
  return translate(
    plural && !fallback.endsWith('s') ? `${fallback}s` : fallback
  );
}

export function kindIcon(
  kind: string,
  campaignNetwork?: string | null
): IconName {
  const format = postFormat(kind);
  if (format) {
    const network = networkKeyFromKind(kind) ?? campaignNetwork ?? null;
    if (format === 'reel' && isInstagram(network)) {
      return 'Social-InstagramReels';
    }
    if (format === 'story' && isInstagram(network)) {
      return 'Social-InstagramStories';
    }
    return networkIcon(network) ?? KIND_ICONS[kind] ?? FALLBACK_ICON;
  }
  return KIND_ICONS[kind] ?? FALLBACK_ICON;
}

function statePhrase(state: DeliverableState): string {
  return translate(STATE_PHRASE[state] ?? STATE_PHRASE.not_submitted);
}

function latestActionDate(values: string[]): string | null {
  const latest = maxBy(
    values.filter((value) => dayjs.utc(value).isValid()),
    (value) => dayjs.utc(value).valueOf()
  );
  return latest
    ? formattedDate(new Date(latest), MONTH_SHORT_DAY_YEAR_FORMAT)
    : null;
}

function isApprovedState(state: DeliverableState): boolean {
  return state === 'accepted' || state === 'posted';
}

function storyFrameCount(
  kind: string,
  slots: CreatorDeliverable[],
  minimumStoryFrames: number | null | undefined
): number | null {
  if (kind !== 'mixed_media') {
    return null;
  }
  const uploaded = slots
    .map((slot) => slot.frameCount)
    .filter((count): count is number => count != null && count > 0);
  if (uploaded.length > 0 && uploaded.every((count) => count === uploaded[0])) {
    return uploaded[0];
  }
  if (minimumStoryFrames && minimumStoryFrames > 0) {
    return minimumStoryFrames;
  }
  return uploaded.length > 0 ? (max(uploaded) ?? null) : null;
}

function kindSortValue(kind: string): [number, string] {
  const index = (KIND_ORDER as readonly string[]).indexOf(kind);
  return [index === -1 ? KIND_ORDER.length : index, kind];
}

function showsRound(state: DeliverableState): boolean {
  return state === 'pending' || state === 'rejected';
}

function groupKey(slot: CreatorDeliverable): string {
  if (showsRound(slot.state)) {
    return `${slot.state}:${slot.round ?? 1}`;
  }
  return slot.state;
}

function groupsForKind(
  slots: CreatorDeliverable[],
  contentDue: string | null
): KindGroup[] {
  return sortBy(
    Object.values(groupBy(slots, groupKey)).map((groupSlots) => {
      const {state} = groupSlots[0];
      const round = showsRound(state) ? (groupSlots[0].round ?? 1) : null;
      const dateSuffix = dateSuffixForGroup(groupSlots, contentDue);
      return {
        state,
        round,
        summary: summarizeGroup(groupSlots.length, state, round, dateSuffix),
        dateSuffix,
        tagColor: tagColorForDeliverableState(state),
      };
    }),
    [
      (group) => STATE_ORDER.indexOf(group.state),
      (group) => -(group.round ?? 0),
    ]
  );
}

function summarizeGroup(
  count: number,
  state: DeliverableState,
  round: number | null,
  dateSuffix: string | null
): string {
  const summary = `${count} ${statePhrase(state)}`;
  const withRound = round == null ? summary : `${summary} · V${round}`;
  return dateSuffix ? `${withRound} · ${dateSuffix}` : withRound;
}

function dateSuffixForGroup(
  slots: CreatorDeliverable[],
  contentDue: string | null
): string | null {
  const actionDates = slots
    .map((slot) => slot.latestFeedbackAt)
    .filter((value): value is string => !!value);
  if (actionDates.length > 0) {
    return latestActionDate(actionDates);
  }

  const allAwaitingUpload = slots.every(
    (slot) => slot.state === 'not_submitted'
  );
  if (!allAwaitingUpload) {
    return null;
  }

  const due = slots.find((slot) => slot.dueOn)?.dueOn ?? contentDue;
  return due
    ? translate('Due {date}', {
        date: formattedUtcDate(due, MONTH_SHORT_DAY_YEAR_FORMAT),
      })
    : null;
}

export function rollupDeliverables(
  deliverables: CreatorDeliverable[],
  contentDue: string | null,
  campaignNetwork?: string | null,
  minimumStoryFrames?: number | null
): KindRollup[] {
  const slotsByKind = groupBy(
    deliverables.map((raw) =>
      raw.state === 'accepted' && SWAP_MATCH_KINDS.has(raw.kind)
        ? {...raw, state: 'posted' as const}
        : raw
    ),
    'kind'
  );

  return sortBy(Object.keys(slotsByKind), [
    (kind) => kindSortValue(kind)[0],
    (kind) => kindSortValue(kind)[1],
  ]).map((kind) => {
    const slots = slotsByKind[kind];
    const approvedCount = slots.filter((slot) =>
      isApprovedState(slot.state)
    ).length;
    return {
      kind,
      icon: kindIcon(kind, campaignNetwork),
      label: kindLabel(kind, campaignNetwork, slots.length),
      approvedCount,
      contractedCount: slots.length,
      isComplete: slots.length > 0 && approvedCount === slots.length,
      frameCount: storyFrameCount(kind, slots, minimumStoryFrames),
      groups: groupsForKind(slots, contentDue),
    };
  });
}
